import { Modal, Select, message } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import './style.less';
import { Icon } from 'shared/components/Icon';
import { useAppSelector } from 'shared/store';
import { Button } from 'shared/components/Button';
import { postFinalCallReport } from 'shared/api';
import {
  selectCallDispositions,
  selectCallSentiments,
} from 'shared/store/outbound-slice';
import log from 'shared/utils/log';

export const DispositionModal: React.FC<{
  visible?: boolean;
  setVisible: () => void;
  close: () => void;
}> = ({ visible, setVisible, close }) => {
  const { detailTask: dialTask } = useAppSelector(s => s.outbound);
  const dispositions = useAppSelector(selectCallDispositions).filter(d => !d.deleted);
  const sentiments = useAppSelector(selectCallSentiments);

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState<string | null | undefined>();
  const [selectedDispositionId, setSelectedDispositionId] = useState<string | null>(null);
  const [selectedSentimentId, setSelectedSentimentId] = useState<string | null>(null);

  useEffect(() => {
    if (dialTask?.callSid !== submitted) {
      setSubmitted(null);
    }
  }, [dialTask, submitted]);

  const handleSubmit = useCallback(async () => {
    if (!selectedDispositionId || !dialTask?.id) {
      log.error(
        'Must have a connected call and select a call disposition before submitting call report.',
      );
      return;
    }

    setLoading(true);

    try {
      await postFinalCallReport(dialTask?.id as string, {
        notes: '',
        dispositionId: selectedDispositionId,
        sentimentId: selectedSentimentId,
      });

      message.success('Disposition logged');
    } catch (err) {
      log.error('Error submitting call feedback', err);
      message.error('Error submitting call feedback');
      return;
    }

    setSubmitted(dialTask.callSid);
    setLoading(false);

    setSelectedDispositionId(null);
    setVisible();
  }, [dialTask, selectedDispositionId, selectedSentimentId, setVisible]);

  return (
    <Modal visible={visible} className="DispositionModal" centered onCancel={close}>
      <div className="DispositionModal__body">
        <div className="DispositionModal__icon">
          <Icon icon="phoneOutline" width="24" height="24" />
        </div>
        <div className="DispositionModal__title">Disposition not logged</div>
        <div className="DispositionModal__description">
          Are you sure you want to close this call without a disposition?
        </div>
        <div className="DispositionModal__disposition-wrap">
          <Select
            className="DispositionModal__disposition"
            placeholder={'Choose disposition'}
            onChange={d => setSelectedDispositionId(d)}
            value={selectedDispositionId || undefined}
            dropdownClassName={'DispositionModal__disposition-dropdown'}
          >
            {dispositions.map(d => {
              return (
                <Select.Option key={d.id} value={d.id}>
                  {d.label}
                </Select.Option>
              );
            })}
          </Select>
          <Icon icon="chevron" width={16} height={16} />
        </div>
        {!!sentiments.length && (
          <div className="DispositionModal__disposition-wrap">
            <Select
              className="DispositionModal__disposition"
              placeholder={'Choose sentiment'}
              onChange={s => setSelectedSentimentId(s)}
              value={selectedSentimentId || undefined}
              dropdownClassName={'DispositionModal__disposition-dropdown'}
            >
              {sentiments.map(s => {
                return (
                  <Select.Option key={s.id} value={s.id}>
                    {s.label}
                  </Select.Option>
                );
              })}
            </Select>
            <Icon icon="chevron" width={16} height={16} />
          </div>
        )}
      </div>
      <div className="DispositionModal__footer">
        <Button
          type="primary"
          size="small"
          className="DispositionModal__button"
          disabled={!selectedDispositionId || loading}
          onClick={handleSubmit}
        >
          Add disposition
        </Button>
        <Button
          type="ghost"
          size="small"
          disabled={loading}
          onClick={setVisible}
          className="DispositionModal__button"
        >
          Close Call
        </Button>
      </div>
    </Modal>
  );
};
